import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Compass, Heart, Sparkles } from "lucide-react";

const WHAT_BRINGS = [
  { id: "myself", label: "I need support for myself" },
  { id: "someone", label: "I'm here for someone I love" },
  { id: "curious", label: "I'm just exploring" },
  { id: "healing", label: "I'm on a healing journey" },
];

const FEELINGS = [
  { id: "overwhelmed", label: "Overwhelmed", color: "#C86B55" },
  { id: "anxious", label: "Anxious", color: "#D2A679" },
  { id: "sad", label: "Sad", color: "#8A918C" },
  { id: "hopeful", label: "Hopeful", color: "#B5C7A3" },
  { id: "calm", label: "Calm", color: "#4A6B53" },
  { id: "confused", label: "Confused", color: "#E5B95F" },
];

const WHAT_HELPS = [
  { id: "talk", label: "Talk to someone", route: "/talk" },
  { id: "calm", label: "Find some calm", route: "/calm" },
  { id: "journal", label: "Write it out", route: "/journal" },
  { id: "understand", label: "Understand what's happening", route: "/understand" },
  { id: "affirmation", label: "Hear something kind", route: "/affirmations" },
  { id: "checkin", label: "Just check in with myself", route: "/check-in" },
];

export default function OnboardingFlow({ onComplete }) {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({ what_brings_you: "", feeling_now: "", what_helps: "" });
  const navigate = useNavigate();

  const handleFinish = async () => {
    try {
      await api.post(`/onboarding`, answers);
    } catch (e) {
      // Not authenticated - that's fine, just skip server save
      console.log("Onboarding saved locally only");
    }
    localStorage.setItem("sovha_onboarded", "true");
    const help = WHAT_HELPS.find(h => h.id === answers.what_helps);
    onComplete();
    if (help) {
      navigate(help.route);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-earth-50 flex items-center justify-center px-4" data-testid="onboarding-flow">
      <div className="max-w-lg w-full">
        {/* Progress */}
        <div className="flex justify-center gap-2 mb-10">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                s <= step ? "w-12 bg-terracotta" : "w-6 bg-earth-200"
              }`}
            />
          ))}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div className="text-center animate-fade-in-up" data-testid="onboarding-step-1">
            <Compass size={32} className="text-terracotta mx-auto mb-4" />
            <h2 className="font-heading text-3xl text-earth-800 mb-2">What brings you to Sovha?</h2>
            <p className="text-earth-500 text-sm mb-8">No wrong answers. This just helps us guide you.</p>
            <div className="space-y-3">
              {WHAT_BRINGS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => { setAnswers(a => ({...a, what_brings_you: opt.id})); setStep(2); }}
                  data-testid={`onboarding-brings-${opt.id}`}
                  className={`w-full text-left px-6 py-4 rounded-2xl border transition-all duration-300 ${
                    answers.what_brings_you === opt.id
                      ? "border-terracotta bg-terracotta-light"
                      : "border-earth-200/50 bg-white hover:bg-earth-100"
                  }`}
                >
                  <span className="text-earth-800 font-medium">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="text-center animate-fade-in-up" data-testid="onboarding-step-2">
            <Heart size={32} className="text-sage mx-auto mb-4" />
            <h2 className="font-heading text-3xl text-earth-800 mb-2">How are you feeling right now?</h2>
            <p className="text-earth-500 text-sm mb-8">Right now, in this moment.</p>
            <div className="grid grid-cols-2 gap-3">
              {FEELINGS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => { setAnswers(a => ({...a, feeling_now: f.id})); setStep(3); }}
                  data-testid={`onboarding-feeling-${f.id}`}
                  className="px-5 py-4 rounded-2xl border transition-all duration-300 hover:scale-[1.02]"
                  style={{
                    backgroundColor: answers.feeling_now === f.id ? f.color + "20" : f.color + "08",
                    borderColor: answers.feeling_now === f.id ? f.color : "transparent",
                  }}
                >
                  <span className="font-medium" style={{ color: f.color }}>{f.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="text-center animate-fade-in-up" data-testid="onboarding-step-3">
            <Sparkles size={32} className="text-warmyellow mx-auto mb-4" />
            <h2 className="font-heading text-3xl text-earth-800 mb-2">What would help most today?</h2>
            <p className="text-earth-500 text-sm mb-8">We'll take you right there.</p>
            <div className="space-y-3">
              {WHAT_HELPS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => { setAnswers(a => ({...a, what_helps: opt.id})); }}
                  data-testid={`onboarding-helps-${opt.id}`}
                  className={`w-full text-left px-6 py-4 rounded-2xl border transition-all duration-300 ${
                    answers.what_helps === opt.id
                      ? "border-warmyellow bg-[#FFF8E7]"
                      : "border-earth-200/50 bg-white hover:bg-earth-100"
                  }`}
                >
                  <span className="text-earth-800 font-medium">{opt.label}</span>
                </button>
              ))}
            </div>
            {answers.what_helps && (
              <button
                onClick={handleFinish}
                data-testid="onboarding-finish-btn"
                className="mt-8 bg-terracotta text-earth-50 rounded-full px-8 py-4 font-medium hover:-translate-y-1 hover:shadow-lg transition-all duration-300 inline-flex items-center gap-2"
              >
                Let's go <ArrowRight size={18} />
              </button>
            )}
          </div>
        )}

        {/* Skip */}
        <div className="text-center mt-6">
          <button
            onClick={() => { localStorage.setItem("sovha_onboarded", "true"); onComplete(); }}
            data-testid="onboarding-skip-btn"
            className="text-earth-400 text-sm hover:text-earth-600 transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
